// A small rich-text editor for email copy: bold / italic / underline / strike, links, bullet and
// numbered lists, text colour, personalisation fields. Writes the safe HTML subset the renderer
// accepts (sanitizeRich) — pasted Word / Google Docs content is cleaned the same way.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { sanitizeRich, MERGE_FIELDS } from '../../lib/emailRender.js';

const MERGE_LABEL = { firstName: 'First name', contactName: 'Full name', company: 'Company', senderName: 'Sender name' };
const SWATCHES = ['#0F172A', '#334155', '#64748B', '#111827', '#B91C1C', '#C2410C', '#047857', '#1D4ED8', '#7C3AED', '#FFFFFF'];

function Btn({ title, onPress, active, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(e) => {
        e.preventDefault(); // keep the text selection
        onPress();
      }}
      className={`flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-[13px] ${active ? 'bg-accent text-white' : 'text-text-secondary hover:bg-surface-raised hover:text-text-primary'}`}
    >
      {children}
    </button>
  );
}

/** Formatting toolbar acting on whichever editor holds the selection. */
export function Toolbar({ editorRef, compact = false }) {
  const [menu, setMenu] = useState(null); // 'link' | 'color' | 'merge'
  const [url, setUrl] = useState('');
  const saved = useRef(null);
  const [, force] = useState(0);
  // Let the editor pick up the change through its own input handler (keeps it in sync).
  const changed = () => editorRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
  const exec = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    changed();
    force((n) => n + 1);
  };
  const state = (cmd) => {
    try {
      return document.queryCommandState(cmd);
    } catch {
      return false;
    }
  };
  useEffect(() => {
    const h = () => force((n) => n + 1);
    document.addEventListener('selectionchange', h);
    return () => document.removeEventListener('selectionchange', h);
  }, []);
  const keepSelection = () => {
    const sel = window.getSelection();
    if (sel?.rangeCount && editorRef.current?.contains(sel.anchorNode)) saved.current = sel.getRangeAt(0).cloneRange();
  };
  const restore = () => {
    editorRef.current?.focus();
    if (saved.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(saved.current);
    }
  };
  const open = (m) => {
    keepSelection();
    if (m === 'link') {
      const a = window.getSelection()?.anchorNode?.parentElement?.closest?.('a');
      setUrl(a?.getAttribute('href') || '');
    }
    setMenu(menu === m ? null : m);
  };

  return (
    <div className="relative flex flex-wrap items-center gap-0.5" onMouseDown={(e) => e.target.tagName !== 'INPUT' && e.preventDefault()}>
      <Btn title="Bold (⌘B)" active={state('bold')} onPress={() => exec('bold')}><strong>B</strong></Btn>
      <Btn title="Italic (⌘I)" active={state('italic')} onPress={() => exec('italic')}><em className="font-serif">I</em></Btn>
      <Btn title="Underline (⌘U)" active={state('underline')} onPress={() => exec('underline')}><span className="underline">U</span></Btn>
      {!compact && <Btn title="Strikethrough" active={state('strikeThrough')} onPress={() => exec('strikeThrough')}><span className="line-through">S</span></Btn>}
      <span className="mx-1 h-4 w-px bg-line" />
      <Btn title="Link" active={menu === 'link'} onPress={() => open('link')}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></svg>
      </Btn>
      <Btn title="Bulleted list" active={state('insertUnorderedList')} onPress={() => exec('insertUnorderedList')}>•≡</Btn>
      {!compact && <Btn title="Numbered list" active={state('insertOrderedList')} onPress={() => exec('insertOrderedList')}>1≡</Btn>}
      <Btn title="Text colour" active={menu === 'color'} onPress={() => open('color')}>
        <span className="font-semibold" style={{ borderBottom: '3px solid #B91C1C', lineHeight: 1 }}>A</span>
      </Btn>
      <Btn title="Clear formatting" onPress={() => exec('removeFormat')}>⌫</Btn>
      <span className="mx-1 h-4 w-px bg-line" />
      <Btn title="Insert a personalisation field" active={menu === 'merge'} onPress={() => open('merge')}>{'{ }'}</Btn>

      {menu === 'link' && (
        <form
          className="absolute left-0 top-full z-30 mt-1 flex w-80 gap-1 rounded-md border border-line bg-surface p-2 shadow-lg"
          onSubmit={(e) => {
            e.preventDefault();
            restore();
            const u = url.trim();
            if (!u) document.execCommand('unlink');
            else {
              const href = /^(https?:|mailto:|tel:|\{)/i.test(u) ? u : `https://${u}`;
              if (window.getSelection()?.isCollapsed) document.execCommand('insertHTML', false, `<a href="${href.replace(/"/g, '&quot;')}">${href.replace(/</g, '&lt;')}</a>`);
              else document.execCommand('createLink', false, href);
            }
            setMenu(null);
            changed();
          }}
        >
          <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (empty removes the link)" className="min-w-0 flex-1 rounded border border-line px-2 py-1 text-sm outline-none focus:border-accent" />
          <button className="rounded bg-accent px-2 text-xs font-medium text-white">Apply</button>
        </form>
      )}
      {menu === 'color' && (
        <div className="absolute left-0 top-full z-30 mt-1 flex w-56 flex-wrap gap-1.5 rounded-md border border-line bg-surface p-2 shadow-lg">
          {SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              onMouseDown={(e) => {
                e.preventDefault();
                restore();
                exec('foreColor', c);
                setMenu(null);
              }}
              className="h-6 w-6 rounded-full border border-line"
              style={{ background: c }}
            />
          ))}
          <label className="flex items-center gap-1 text-xs text-text-muted">
            Custom
            <input type="color" onChange={(e) => { restore(); exec('foreColor', e.target.value); }} className="h-6 w-8 cursor-pointer border-0 bg-transparent p-0" />
          </label>
        </div>
      )}
      {menu === 'merge' && (
        <div className="absolute left-0 top-full z-30 mt-1 w-52 rounded-md border border-line bg-surface p-1 shadow-lg">
          {MERGE_FIELDS.map((f) => (
            <button
              key={f}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                restore();
                exec('insertText', `{${f}}`);
                setMenu(null);
              }}
              className="flex w-full justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-surface-raised"
            >
              {MERGE_LABEL[f]} <code className="text-xs text-text-muted">{`{${f}}`}</code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The editable area. `html` is controlled loosely: outside changes (undo, AI draft) replace the
 * content; the user's own typing never re-renders it, so the caret never jumps.
 */
export function Editable({ html, onChange, editorRef, style, className = '', autoFocus = false, caretAt = null, placeholder = '', onKeyDown }) {
  const local = useRef(null);
  const ref = editorRef || local;
  const last = useRef(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    if (html !== last.current) {
      // Blank content (e.g. "<p></p>") renders as truly empty so the placeholder shows and the box
      // keeps its height — an empty paragraph is 0px tall and can't be clicked into.
      ref.current.innerHTML = /^(\s|<p>\s*(<br>)?\s*<\/p>)*$/.test(html || '') ? '' : sanitizeRich(html);
      last.current = html;
    }
  }, [html]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!autoFocus || !ref.current) return;
    ref.current.focus();
    // Put the caret where they clicked (falls back to the end of the text).
    let r = caretAt && document.caretRangeFromPoint ? document.caretRangeFromPoint(caretAt.x, caretAt.y) : null;
    if (!r || !ref.current.contains(r.startContainer)) {
      r = document.createRange();
      r.selectNodeContents(ref.current);
      r.collapse(false);
    }
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const emit = () => {
    const clean = sanitizeRich(ref.current.innerHTML);
    if (clean === last.current || (!last.current && !clean)) return; // nothing changed (e.g. a blur)
    last.current = clean;
    onChange(clean);
  };
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      data-placeholder={placeholder}
      className={`t8-editable outline-none ${className}`}
      style={style}
      onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
      onInput={emit}
      onBlur={emit}
      onKeyDown={onKeyDown}
      onPaste={(e) => {
        e.preventDefault();
        const h = e.clipboardData.getData('text/html');
        const t = e.clipboardData.getData('text/plain');
        const safe = h ? sanitizeRich(h) : t.split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p>`).join('');
        document.execCommand('insertHTML', false, safe);
        emit();
      }}
    />
  );
}

/** Toolbar + editable box (used in the side panel for columns / image + text). */
export default function RichText({ html, onChange, minHeight = 80, placeholder }) {
  const ref = useRef(null);
  return (
    <div className="rounded-md border border-line bg-surface focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/30">
      <div className="border-b border-line px-1 py-0.5">
        <Toolbar editorRef={ref} compact />
      </div>
      <Editable editorRef={ref} html={html} onChange={onChange} placeholder={placeholder} className="px-3 py-2 text-sm leading-relaxed" style={{ minHeight }} />
    </div>
  );
}
